import { CssBaseline } from '@mui/material';
import { useEffect } from 'react';
import { useNavigate, useRoutes } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import i18n from './config/i18n/i18n';
import { resolveInitialLocale } from './common/utils/resolveInitialLocale';
import { setDayjsLocale } from './config/dayjs';
import { routes } from './routes/router';
import axiosInstance from './config/axiosInstance';
import { useLoadingStatusActions } from './hooks/useLoadingStatus';
import { localStorageService } from './services/localStorageService/localStorageService';
import { sendEventToReactNative } from './common/utils/reactNativeHelpers';
import { WebViewEvents } from './common/enums/webViewEvents';
import { useUserActions } from './hooks/useUser';
import { authService } from './services/auth/AuthService';
import { ROUTES } from './constants/routes';

function App() {
  const element = useRoutes(routes);

  const { onChangeLoadingButtonStatus } = useLoadingStatusActions();
  const { onChangeMobileUserAuthError } = useUserActions();

  const navigate = useNavigate();

  useEffect(() => {
    const language = resolveInitialLocale();
    i18n.changeLanguage(language);
    setDayjsLocale(language);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const handleMessage = async (event: any) => {
      const messageData = event.detail;
      if (messageData) {
        if ('auth' in messageData && messageData.auth === 'dismiss') {
          onChangeLoadingButtonStatus();
          return;
        }

        if (messageData.action === WebViewEvents.REFRESH) {
          window.location.reload();
        }

        if (messageData.action === WebViewEvents.AUTH_ERROR) {
          onChangeMobileUserAuthError(messageData.errorMessage);
          onChangeLoadingButtonStatus();
        }

        if (messageData.action === WebViewEvents.ACCEPT_BIOMETRIC_AUTH) {
          const token = await authService.getBiometricToken(
            messageData.uniqueId,
          );

          sendEventToReactNative(
            WebViewEvents.SEND_BIOMETRIC_TOKEN,
            undefined,
            token,
          );
        }

        if (messageData.action === WebViewEvents.BIOMETRIC_AUTH) {
          let token: string | undefined;
          try {
            token = await authService.biometricAuth(messageData.token);
          } catch (error) {
            // Used or revoked one-time token: the app drops it so it stops
            // offering a biometric sign-in that can't succeed.
            sendEventToReactNative(WebViewEvents.BIOMETRIC_AUTH_FAILED);
            Sentry.captureException(error);
            return;
          }
          sendEventToReactNative(WebViewEvents.SUCCESS_AUTH, undefined, token);

          // Check for saved redirect URL
          const redirectUrl = localStorageService().getRedirectUrl();
          if (redirectUrl) {
            localStorageService().removeRedirectUrl();
            navigate(redirectUrl);
          } else {
            navigate(`/${ROUTES.CABINET}`);
          }
        }

        if (localStorageService().getAccessToken()) {
          try {
            await axiosInstance.post('/devices/add', {
              ...messageData.deviceInfo,
              messagingToken: messageData.messagingToken,
            });
            sendEventToReactNative(WebViewEvents.SUCCESS_AUTH);
          } catch (error) {
            Sentry.captureException(error);
          }
        }
      }
    };

    window.addEventListener('webview-message', handleMessage);

    return () => {
      window.removeEventListener('webview-message', handleMessage);
    };
    // eslint-disable-next-line
  }, []);

  return (
    <>
      <CssBaseline />
      {element}
    </>
  );
}

export default App;
